/**
 * SwitchAccountTab.jsx
 *
 * Lets users:
 *   1. Instantly switch between roles they already own.
 *   2. Submit a request to add a new role.
 *
 * Drop into: src/pages/account/sections/ (or wherever your other tab sections live)
 */

import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../../context/AuthContext";
import {
  switchRole,
  requestRoleSwitch,
  getRoleSwitchStatus,
} from "../../../../utils/roleSwitchApi";
import "./SwitchAccountTab.css";

// ── Config ────────────────────────────────────────────────────────────────────

const ROLE_META = {
  student: {
    label: "Student",
    icon: "🎓",
    description:
      "Browse tutors, book sessions, and track your learning journey.",
    color: "#4f6ef7",
  },
  tutor: {
    label: "Tutor",
    icon: "📚",
    description:
      "Create your tutor profile, list subjects, and accept bookings.",
    color: "#16a34a",
  },
  institution: {
    label: "Learning Centre",
    icon: "🏫",
    description:
      "Manage your center, enrol students, and grow your institution.",
    color: "#d97706",
  },
};

const ALL_ROLES = ["student", "tutor", "institution"];

// Role-specific fields collected when requesting a new role, so the new
// role has real data to display once approved instead of falling back to
// placeholders like "Training Center".
const ROLE_REQUEST_FIELDS = {
  institution: [
    { name: "academyName", label: "Training Center Name" },
    { name: "adminFirstName", label: "Admin First Name" },
    { name: "adminLastName", label: "Admin Last Name" },
    { name: "adminEmail", label: "Admin Email", type: "email" },
    { name: "adminPhone", label: "Admin Phone", type: "tel" },
  ],
  tutor: [
    { name: "degree", label: "Degree" },
    { name: "fieldOfStudy", label: "Field of Study" },
    { name: "schoolName", label: "School Name" },
  ],
  student: [],
};

// ── Sub-components ────────────────────────────────────────────────────────────

const RoleCard = ({
  role,
  isActive,
  isOwned,
  isPending,
  onSwitch,
  onRequest,
  loadingRole,
}) => {
  const meta = ROLE_META[role];
  const isLoading = loadingRole === role;

  const handleClick = () => {
    if (isActive || isLoading) return;
    if (isOwned) onSwitch(role);
    else if (!isPending) onRequest(role);
  };

  let buttonLabel = "Switch";
  let buttonClass = "rsa-btn rsa-btn--switch";
  let cardClass = "rsa-card";

  if (isActive) {
    buttonLabel = "Active";
    buttonClass = "rsa-btn rsa-btn--active";
    cardClass += " rsa-card--active";
  } else if (isPending) {
    buttonLabel = "Pending approval";
    buttonClass = "rsa-btn rsa-btn--pending";
  } else if (!isOwned) {
    buttonLabel = "Add account";
    buttonClass = "rsa-btn rsa-btn--add";
  }

  return (
    <div className={cardClass} style={{ "--role-color": meta.color }}>
      <div className="rsa-card__icon">{meta.icon}</div>
      <div className="rsa-card__body">
        <div className="rsa-card__header">
          <h4 className="rsa-card__title">{meta.label}</h4>
          {isActive && (
            <span className="rsa-badge rsa-badge--active">Active</span>
          )}
          {isPending && (
            <span className="rsa-badge rsa-badge--pending">Under review</span>
          )}
          {isOwned && !isActive && (
            <span className="rsa-badge rsa-badge--owned">Available</span>
          )}
        </div>
        <p className="rsa-card__desc">{meta.description}</p>
      </div>
      <button
        className={buttonClass}
        onClick={handleClick}
        disabled={isActive || isPending || !!loadingRole}
        aria-label={`${buttonLabel} ${meta.label}`}
      >
        {/* {loading === role ? (
          <span className="rsa-spinner" />
        ) : (
          buttonLabel
        )} */}
        {isLoading ? <span className="rsa-spinner" /> : buttonLabel}
      </button>
    </div>
  );
};

const ConfirmModal = ({ role, action, onConfirm, onCancel, loading }) => {
  const meta = ROLE_META[role];
  const isSwitch = action === "switch";

  return (
    <div className="rsa-modal-overlay" role="dialog" aria-modal="true">
      <div className="rsa-modal">
        <div className="rsa-modal__icon">{meta.icon}</div>
        <h3 className="rsa-modal__title">
          {isSwitch ? `Switch to ${meta.label}?` : `Add ${meta.label} account?`}
        </h3>
        <p className="rsa-modal__body">
          {isSwitch
            ? `Your current session will switch to the ${meta.label} view. You can switch back any time.`
            : `We'll send a request to our team to review and activate your ${meta.label} account. This usually takes 1–2 business days.`}
        </p>
        <div className="rsa-modal__actions">
          <button
            className="rsa-btn rsa-btn--ghost"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="rsa-btn rsa-btn--confirm"
            style={{ "--role-color": meta.color }}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <span className="rsa-spinner rsa-spinner--sm" />
            ) : isSwitch ? (
              "Yes, switch"
            ) : (
              "Submit request"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const RequestDetailsModal = ({ role, fields, onSubmit, onCancel, loading }) => {
  const meta = ROLE_META[role];
  const [values, setValues] = useState(
    fields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {}),
  );

  const missingFields = fields.filter((f) => !values[f.name].trim());
  const isValid = missingFields.length === 0;

  const handleChange = (name) => (e) =>
    setValues((prev) => ({ ...prev, [name]: e.target.value }));

  return (
    <div className="rsa-modal-overlay" role="dialog" aria-modal="true">
      <div className="rsa-modal rsa-modal--form">
        <div className="rsa-modal__icon">{meta.icon}</div>
        <h3 className="rsa-modal__title">Add {meta.label} account</h3>
        <p className="rsa-modal__body">
          Fill in your {meta.label.toLowerCase()} details so we can review
          and set up your new account correctly.
        </p>

        <div className="rsa-modal__form">
          {fields.map((field) => (
            <div className="form-group" key={field.name}>
              <label>{field.label}</label>
              <input
                type={field.type || "text"}
                value={values[field.name]}
                onChange={handleChange(field.name)}
              />
            </div>
          ))}
        </div>

        <div className="rsa-modal__actions">
          <button
            className="rsa-btn rsa-btn--ghost"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="rsa-btn rsa-btn--confirm"
            style={{ "--role-color": meta.color }}
            onClick={() => onSubmit(values)}
            disabled={loading || !isValid}
          >
            {loading ? (
              <span className="rsa-spinner rsa-spinner--sm" />
            ) : (
              "Submit request"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const SwitchAccountTab = ({ setStatusModal }) => {
  const { user, updateUser, refreshUser } = useAuth();

  // const [availableRoles, setAvailableRoles] = useState(user?.roles ?? []);
  // ✅ These come DIRECTLY from user context — always in sync after updateUser/refreshUser
  // ✅ Memoized — only recomputes when user object actually changes
  const currentRole = useMemo(() => user?.role ?? "", [user]);
  const availableRoles = useMemo(() => user?.roles ?? [], [user]);
  const [pendingRequest, setPendingRequest] = useState(null);

  const [loadingRole, setLoadingRole] = useState(null); // role string | null
  const [confirm, setConfirm] = useState(null); // { role, action } | null
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    getRoleSwitchStatus()
      .then((data) => {
        setPendingRequest(data.roleSwitchRequest ?? null);
        // Sync roles if server has more than context
        if (data.roles?.length && 
            JSON.stringify(data.roles.sort()) !== JSON.stringify([...availableRoles].sort())) {
          updateUser({ ...user, roles: data.roles });
        }
      })
      .catch((err) => console.warn("[SwitchAccountTab]", err.message));
  }, []); // run once only

  // const currentRole = user?.role;
  const hasPendingRequest = pendingRequest?.status === "pending";

  const handleSwitchClick = (role) => setConfirm({ role, action: "switch" });
  const handleRequestClick = (role) => setConfirm({ role, action: "request" });

  const requestFields = confirm ? ROLE_REQUEST_FIELDS[confirm.role] || [] : [];
  const needsDetailsForm =
    confirm?.action === "request" && requestFields.length > 0;

  const submitRoleRequest = async (role, details) => {
    setConfirmLoading(true);
    setConfirm(null);

    try {
      const { message, roleSwitchRequest } = await requestRoleSwitch(
        role,
        details,
      );
      setPendingRequest(roleSwitchRequest);
      setStatusModal({
        open: true,
        type: "success",
        message: "Request submitted",
        subtext: message,
      });
    } catch (err) {
      setStatusModal({
        open: true,
        type: "error",
        message: "Something went wrong",
        subtext: err.response?.data?.message || err.message || "Please try again.",
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleConfirm = async () => {
  if (!confirm) return;
  const { role, action } = confirm;

  if (action === "switch") setLoadingRole(role);
  setConfirmLoading(true);
  setConfirm(null);

  try {
    if (action === "switch") {
      const { user: updated } = await switchRole(role);

      // ✅ Optimistically update context immediately — no flicker
      updateUser(updated);

      // ✅ Sync localStorage role key so nothing reads stale role
      localStorage.setItem("user", JSON.stringify(updated));

      // ✅ Silent background refresh — don't await it
      refreshUser().catch(() => {});

      setStatusModal({
        open: true,
        type: "success",
        message: `Switched to ${ROLE_META[role].label}`,
        subtext: `You are now using your ${ROLE_META[role].label} account.`,
      });
    } else {
      const { message, roleSwitchRequest } = await requestRoleSwitch(role);
      setPendingRequest(roleSwitchRequest);
      setStatusModal({
        open: true,
        type: "success",
        message: "Request submitted",
        subtext: message,
      });
    }
  } catch (err) {
    setStatusModal({
      open: true,
      type: "error",
      message: "Something went wrong",
      subtext: err.message || "Please try again.",
    });
  } finally {
    setConfirmLoading(false);
    setLoadingRole(null);
  }
};

  return (
    <div className="rsa-root">
      {/* Header */}
      <div className="rsa-header">
        <h2 className="rsa-header__title">Switch Account</h2>
        <p className="rsa-header__sub">
          Use one login to manage all your roles. Switch instantly between
          accounts you own, or request a new account type below.
        </p>
      </div>

      {/* Pending request notice */}
      {hasPendingRequest && (
        <div className="rsa-notice">
          <span className="rsa-notice__icon">⏳</span>
          <div>
            <strong>
              {ROLE_META[pendingRequest.requestedRole]?.label ??
                pendingRequest.requestedRole}{" "}
              account under review
            </strong>
            <p>
              We received your request on{" "}
              {new Date(pendingRequest.requestedAt).toLocaleDateString(
                "en-NG",
                {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                },
              )}
              . We'll notify you once it's approved.
            </p>
          </div>
        </div>
      )}

      {/* Role cards */}
      <div className="rsa-grid">
        {ALL_ROLES.map((role) => (
          <RoleCard
            key={role}
            role={role}
            isActive={currentRole === role}
            isOwned={availableRoles.includes(role)}
            isPending={
              hasPendingRequest && pendingRequest.requestedRole === role
            }
            onSwitch={handleSwitchClick}
            onRequest={handleRequestClick}
            loading={loadingRole}
          />
        ))}
      </div>

      {/* Info footer */}
      <p className="rsa-footer-note">
        Switching accounts preserves all your data. Profile, documents, and
        settings are saved separately for each account type.
      </p>

      {/* Confirm / details modal */}
      {confirm && needsDetailsForm ? (
        <RequestDetailsModal
          role={confirm.role}
          fields={requestFields}
          onSubmit={(details) => submitRoleRequest(confirm.role, details)}
          onCancel={() => setConfirm(null)}
          loading={confirmLoading}
        />
      ) : (
        confirm && (
          <ConfirmModal
            role={confirm.role}
            action={confirm.action}
            onConfirm={handleConfirm}
            onCancel={() => setConfirm(null)}
            loading={confirmLoading}
          />
        )
      )}
    </div>
  );
};

export default SwitchAccountTab;
